import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { memo, useState } from "react";
import { 
  Heart, 
  Users, 
  Activity, 
  Briefcase, 
  Flame, 
  Coins, 
  Search,
  Sparkles,
  Car,
  Home,
  Smartphone,
  Sofa,
  Wrench,
  ShoppingBag
} from "lucide-react";

// Web3 / Personal Categories
const personalCategories = [
  "romance",
  "friendship",
  "activities",
  "professional",
  "encounters",
  "blockchain",
  "missed-connections",
  "events"
];

// Traditional Marketplace Categories
const marketplaceCategories = [
  "vehicles",
  "realEstate",
  "electronics",
  "furniture",
  "jobs",
  "services",
  "forSale"
];

const allCategories = [...personalCategories, ...marketplaceCategories];

const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  // Personal
  romance: Heart,
  friendship: Users,
  activities: Activity,
  professional: Briefcase,
  encounters: Flame,
  blockchain: Coins,
  "missed-connections": Search,
  events: Sparkles,
  // Marketplace
  vehicles: Car,
  realEstate: Home,
  electronics: Smartphone,
  furniture: Sofa,
  jobs: Briefcase,
  services: Wrench,
  forSale: ShoppingBag
};

const categoryColors: Record<string, string> = {
  // Personal (Warm/Vibrant tones)
  romance: "bg-rose-500/10 text-rose-600 hover:bg-rose-500/20",
  friendship: "bg-sky-500/10 text-sky-600 hover:bg-sky-500/20",
  activities: "bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20",
  professional: "bg-slate-500/10 text-slate-600 hover:bg-slate-500/20",
  encounters: "bg-orange-600/10 text-orange-700 hover:bg-orange-600/20",
  blockchain: "bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20",
  "missed-connections": "bg-purple-500/10 text-purple-600 hover:bg-purple-500/20",
  events: "bg-amber-500/10 text-amber-600 hover:bg-amber-500/20",
  
  // Marketplace (Cool/Professional tones)
  vehicles: "bg-blue-500/10 text-blue-600 hover:bg-blue-500/20",
  realEstate: "bg-green-500/10 text-green-600 hover:bg-green-500/20",
  electronics: "bg-purple-500/10 text-purple-600 hover:bg-purple-500/20",
  furniture: "bg-amber-500/10 text-amber-600 hover:bg-amber-500/20",
  jobs: "bg-blue-600/10 text-blue-700 hover:bg-blue-600/20",
  services: "bg-orange-500/10 text-orange-600 hover:bg-orange-500/20",
  forSale: "bg-teal-500/10 text-teal-600 hover:bg-teal-500/20"
};

interface CategoryGridProps {
  showAll?: boolean;
  className?: string;
}

// Memoized category item component
const CategoryItem = memo(function CategoryItem({
  category,
  icon: Icon,
  colorClass,
  label,
  isWeb3 = false
}: {
  category: string;
  icon: React.ComponentType<{ className?: string }>;
  colorClass: string;
  label: string;
  isWeb3?: boolean;
}) {
  return (
    <Link href={`/browse?category=${category}`}>
      <a className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${colorClass} relative overflow-hidden group`}>
        {isWeb3 && (
          <div className="absolute top-0 right-0 p-1">
             <span className="flex h-2 w-2 rounded-full bg-primary/70 ring-1 ring-white" title="Web3 Verified"></span>
          </div>
        )}
        <Icon className="w-8 h-8 mb-2 group-hover:scale-110 transition-transform duration-300" />
        <span className="text-sm font-medium text-center">{label}</span>
      </a>
    </Link>
  );
});

CategoryItem.displayName = "CategoryItem";

export const CategoryGrid = memo(function CategoryGrid({ showAll = false, className }: CategoryGridProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'all' | 'personals' | 'marketplace'>('all');

  // Filter categories based on tab or showAll logic
  const getCategories = () => {
    if (activeTab === 'personals') return personalCategories;
    if (activeTab === 'marketplace') return marketplaceCategories;
    
    // Default 'all' view
    if (showAll) return allCategories;
    
    // Preview: 4 personals + 4 marketplace
    return [...personalCategories.slice(0, 4), ...marketplaceCategories.slice(0, 4)];
  };

  const displayCategories = getCategories();

  return (
    <div className={className}>
      {showAll && (
        <div className="flex justify-center gap-4 mb-8">
          <button 
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeTab === 'all' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`}
          >
           {t('common.all')}
          </button>
          <button 
            onClick={() => setActiveTab('personals')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeTab === 'personals' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`}
          >
            Web3 Connections
          </button>
          <button 
            onClick={() => setActiveTab('marketplace')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeTab === 'marketplace' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`}
          >
            Marketplace
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
        {displayCategories.map((category) => {
          const Icon = categoryIcons[category] || ShoppingBag;
          const colorClass = categoryColors[category] || "bg-gray-500/10 text-gray-600 hover:bg-gray-500/20";
          const isWeb3 = personalCategories.includes(category);

          return (
            <CategoryItem
              key={category}
              category={category}
              icon={Icon}
              colorClass={colorClass}
              label={t(`categories.${category}`)}
              isWeb3={isWeb3}
            />
          );
        })}
      </div>
    </div>
  );
});

CategoryGrid.displayName = "CategoryGrid";

// Single category card for use elsewhere
export const CategoryCard = memo(function CategoryCard({ 
  category, 
  count 
}: { 
  category: string; 
  count?: number;
}) {
  const { t } = useTranslation();
  const Icon = categoryIcons[category] || ShoppingBag;
  const colorClass = categoryColors[category] || "bg-gray-500/10 text-gray-600 hover:bg-gray-500/20";

  return (
    <Link href={`/browse?category=${category}`}>
      <a className={`flex items-center gap-3 p-4 rounded-xl transition-all ${colorClass}`}>
        <Icon className="w-6 h-6" />
        <div className="flex-1">
          <span className="font-medium">{t(`categories.${category}`)}</span>
        </div>
        {count !== undefined && (
          <span className="text-sm opacity-70">{count}</span>
        )}
      </a>
    </Link>
  );
});

CategoryCard.displayName = "CategoryCard";

// Dropdown/select for category selection
export function getCategoryOptions(t: (key: string) => string) {
  return allCategories.map(cat => ({
    value: cat,
    label: t(`categories.${cat}`),
    icon: categoryIcons[cat],
  }));
}

export { allCategories as categories, categoryIcons, categoryColors };
